/**
 * Dialog "Importar / Gerar em massa" de templates próprios.
 *
 * Fluxo em 2 fases:
 *   1. Entrada — fonte do JSON:
 *        a) Colar texto
 *        b) Upload de arquivo .json
 *        c) A partir de um modelo (pré-preenche o JSON de um template existente
 *           pra você duplicar/editar várias variações)
 *   2. Revisão (staging) — cada item vira um card com preview + aprovar/descartar.
 *      Cria só os aprovados na pasta/projeto atual (bulk).
 *
 * Formato JSON esperado: array de objetos
 *   [{ "name", "template_type"?, "title"?, "subtitle"?, "image_url"?,
 *      "text_content"?, "buttons"?: [{"title","url"}] }]
 */

import { useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Loader2, Upload, FileJson, Check, X, Sparkles, AlertTriangle, Trash2,
} from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { bulkCreateCustomTemplates } from '@/services/api';
import type { CustomTemplate, CustomTemplateBulkItem } from '@/types/template-library';

type Mode = 'paste' | 'upload' | 'fromModel';

interface Candidate extends CustomTemplateBulkItem {
    _approved: boolean;
    _error?: string;
}

// Limites espelhados de `CustomTemplateBulkItem` no backend (Pydantic).
// Validar local antes do POST evita 422 com mensagem genérica e dá feedback
// por card. `BUTTON_TITLE_MAX=20` é regra do Messenger, não nossa.
const LIMITS = {
    NAME: 255,
    TITLE: 512,
    SUBTITLE: 512,
    IMAGE_URL: 2000,
    BUTTON_TITLE: 20,
    BUTTON_URL: 2000,
    BUTTON_PAYLOAD: 1000,
    BUTTONS_PER_CARD: 3,
    MAX_ITEMS: 300,
};

/** Validações pré-envio — retorna mensagem de erro legível ou undefined. */
function validateCandidate(c: CustomTemplateBulkItem): string | undefined {
    const ttype = c.template_type === 'button' ? 'button' : 'generic';
    if (ttype === 'generic' && !c.title) return 'Sem título';
    if (ttype === 'button' && !c.text_content && !c.title) return 'Sem texto';
    if ((c.name || '').length > LIMITS.NAME) return `Nome muito longo (máx ${LIMITS.NAME})`;
    if ((c.title || '').length > LIMITS.TITLE) return `Título muito longo (máx ${LIMITS.TITLE})`;
    if ((c.subtitle || '').length > LIMITS.SUBTITLE) return `Subtítulo muito longo (máx ${LIMITS.SUBTITLE})`;
    if ((c.image_url || '').length > LIMITS.IMAGE_URL) return `URL da imagem muito longa (máx ${LIMITS.IMAGE_URL})`;
    const btns = c.buttons || [];
    if (btns.length > LIMITS.BUTTONS_PER_CARD) return `Máximo ${LIMITS.BUTTONS_PER_CARD} botões`;
    for (let i = 0; i < btns.length; i++) {
        const b = btns[i];
        const btype = b.type === 'postback' ? 'postback' : 'url';
        if ((b.title || '').length > LIMITS.BUTTON_TITLE) {
            return `Botão ${i + 1}: texto muito longo (máx ${LIMITS.BUTTON_TITLE} caracteres) — Messenger não aceita`;
        }
        if (!b.title) return `Botão ${i + 1}: sem texto`;
        if (btype === 'postback') {
            // Postback dispara webhook (funil), não abre link — não exige URL.
            // Payload deriva do título quando vazio (mesma regra do backend).
            if ((b.payload || '').length > LIMITS.BUTTON_PAYLOAD) {
                return `Botão ${i + 1}: payload muito longo (máx ${LIMITS.BUTTON_PAYLOAD})`;
            }
        } else {
            if ((b.url || '').length > LIMITS.BUTTON_URL) {
                return `Botão ${i + 1}: URL muito longa`;
            }
            if (!b.url) return `Botão ${i + 1}: sem URL`;
        }
    }
    return undefined;
}

/** Traduz mensagens técnicas do Pydantic pra PT-BR conciso. */
function translatePydanticMessage(msg: string): string {
    if (!msg) return 'Inválido';
    const longMatch = msg.match(/at most (\d+)/i);
    if (longMatch) return `máx ${longMatch[1]} caracteres`;
    const shortMatch = msg.match(/at least (\d+)/i);
    if (shortMatch) return `mín ${shortMatch[1]} caracteres`;
    if (/field required/i.test(msg)) return 'campo obrigatório';
    if (/valid url/i.test(msg)) return 'URL inválida';
    if (/valid email/i.test(msg)) return 'email inválido';
    if (/extra inputs/i.test(msg)) return 'campo desconhecido';
    return msg;
}

/** Traduz `field` do backend (`body -> items -> N -> buttons -> M -> title`)
 * em rótulo amigável e extrai o index `N` (do array enviado).
 */
function parseFieldPath(field: string): { itemIdx: number | null; label: string } {
    const parts = field.split(/\s*->\s*/);
    let itemIdx: number | null = null;
    const segs: string[] = [];
    for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        if (p === 'items' && i + 1 < parts.length && /^\d+$/.test(parts[i + 1])) {
            itemIdx = parseInt(parts[i + 1], 10);
            i++; // pula o número
            continue;
        }
        if (p === 'body') continue;
        if (p === 'buttons' && i + 1 < parts.length && /^\d+$/.test(parts[i + 1])) {
            const btnIdx = parseInt(parts[i + 1], 10);
            segs.push(`botão ${btnIdx + 1}`);
            i++;
            continue;
        }
        const labelMap: Record<string, string> = {
            name: 'nome', title: 'título', subtitle: 'subtítulo',
            image_url: 'imagem', text_content: 'texto', url: 'URL',
            template_type: 'tipo',
        };
        segs.push(labelMap[p] || p);
    }
    return { itemIdx, label: segs.join(' / ') || 'campo' };
}

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    projectId: number | null;
    folderId: number | null;
    folderLabel: string;
    /** Templates da pasta atual — pra "a partir de um modelo". */
    baseTemplates: CustomTemplate[];
    onCreated?: () => void;
}

const SAMPLE = `[
  {
    "name": "Celta promo",
    "template_type": "generic",
    "title": "CHEVROLET CELTA",
    "subtitle": "Sem entrada e com análise",
    "image_url": "https://exemplo.com/celta.png",
    "buttons": [
      { "title": "VER OS CARROS", "type": "url", "url": "https://destino.com/celta" }
    ]
  },
  {
    "name": "Boas-vindas postback",
    "template_type": "button",
    "text_content": "Tienes un mensaje nuevo esperándote.",
    "buttons": [
      { "title": "VER AHORA", "type": "postback", "payload": "START_WELCOME" }
    ]
  }
]`;

export default function ImportTemplatesDialog({
    open, onOpenChange, projectId, folderId, folderLabel, baseTemplates, onCreated,
}: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const fileRef = useRef<HTMLInputElement>(null);

    const [mode, setMode] = useState<Mode>('paste');
    const [jsonText, setJsonText] = useState('');
    const [candidates, setCandidates] = useState<Candidate[] | null>(null);
    const [parseError, setParseError] = useState<string | null>(null);

    const reset = () => {
        setMode('paste');
        setJsonText('');
        setCandidates(null);
        setParseError(null);
    };

    const handleOpenChange = (o: boolean) => {
        if (!o) reset();
        onOpenChange(o);
    };

    // ─── Parse + valida o JSON em candidatos ───────────────────
    const processJson = () => {
        setParseError(null);
        let parsed: any;
        try {
            parsed = JSON.parse(jsonText);
        } catch (e: any) {
            setParseError(`JSON inválido: ${e.message}`);
            return;
        }
        const arr = Array.isArray(parsed) ? parsed : [parsed];
        if (arr.length === 0) {
            setParseError('Nenhum item no JSON.');
            return;
        }
        const cands: Candidate[] = arr.map((it: any, i: number) => {
            const name = (it?.name || it?.title || `Modelo ${i + 1}`).toString();
            const ttype = it?.template_type === 'button' ? 'button' : 'generic';
            const buttons = Array.isArray(it?.buttons)
                ? it.buttons
                    .filter((b: any) => b && (b.title || b.url || b.payload))
                    .slice(0, 3)
                    .map((b: any) => {
                        const btype = b?.type === 'postback' ? 'postback' : 'url';
                        return btype === 'postback'
                            ? {
                                title: String(b.title || ''),
                                type: 'postback' as const,
                                payload: String(b.payload || b.title || ''),
                            }
                            : {
                                title: String(b.title || ''),
                                type: 'url' as const,
                                url: String(b.url || ''),
                            };
                    })
                : [];
            const base: CustomTemplateBulkItem = {
                name,
                template_type: ttype,
                title: it?.title ? String(it.title) : undefined,
                subtitle: it?.subtitle ? String(it.subtitle) : undefined,
                image_url: it?.image_url ? String(it.image_url) : undefined,
                text_content: it?.text_content ? String(it.text_content) : undefined,
                buttons,
            };
            const error = validateCandidate(base);
            return { ...base, _approved: !error, _error: error };
        });
        setCandidates(cands);
    };

    // ─── Upload de arquivo ─────────────────────────────────────
    const handleFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = () => {
            setJsonText(String(reader.result || ''));
            setMode('paste'); // mostra o texto carregado pra revisar antes de processar
        };
        reader.readAsText(file);
    };

    // ─── "A partir de um modelo": pré-preenche o JSON ──────────
    const loadFromModel = (tpl: CustomTemplate) => {
        const clean: CustomTemplateBulkItem = {
            name: tpl.name,
            template_type: tpl.template_type,
            title: tpl.title || undefined,
            subtitle: tpl.subtitle || undefined,
            image_url: tpl.image_url || undefined,
            text_content: tpl.text_content || undefined,
            buttons: (tpl.buttons_json || []).map((b) => (
                b.type === 'postback'
                    ? { title: b.title || '', type: 'postback' as const, payload: b.payload || b.title || '' }
                    : { title: b.title || '', type: 'url' as const, url: b.url || '' }
            )),
        };
        // Duplica em 2 pra deixar claro que é pra gerar várias variações
        setJsonText(JSON.stringify([clean, clean], null, 2));
        setMode('paste');
    };

    // ─── Criação em massa dos aprovados ────────────────────────
    // `approvedIndexMap` mapeia o index do array enviado → index original em
    // `candidates`. Usado pra colocar `_error` no card certo quando o backend
    // devolve erros de validação por item.
    const approvedIndexMapRef = useRef<number[]>([]);

    const mutation = useMutation({
        mutationFn: async () => {
            const list = candidates || [];
            const approved: { item: CustomTemplateBulkItem; origIdx: number }[] = [];
            list.forEach((c, idx) => {
                if (!c._approved) return;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { _approved, _error, ...item } = c;
                approved.push({ item, origIdx: idx });
            });
            approvedIndexMapRef.current = approved.map((a) => a.origIdx);
            return bulkCreateCustomTemplates({
                project_id: projectId,
                folder_id: folderId,
                items: approved.map((a) => a.item),
            });
        },
        onSuccess: (r) => {
            toast({
                title: 'Templates criados',
                description: `${r.created} criados${r.skipped ? ` · ${r.skipped} ignorados` : ''}.`,
            });
            queryClient.invalidateQueries({ queryKey: ['custom-templates'] });
            queryClient.invalidateQueries({ queryKey: ['folder-tree'] });
            onCreated?.();
            handleOpenChange(false);
        },
        onError: (e: any) => {
            const data = e?.response?.data;
            const serverErrors: Array<{ field: string; message: string; type: string }> =
                Array.isArray(data?.errors) ? data.errors : [];

            // Mapeia erros do server pros candidatos correspondentes (`_error` no card).
            if (serverErrors.length > 0) {
                const errsByOrigIdx = new Map<number, string[]>();
                for (const er of serverErrors) {
                    const { itemIdx, label } = parseFieldPath(er.field || '');
                    const msg = `${label}: ${translatePydanticMessage(er.message)}`;
                    if (itemIdx === null) continue;
                    const origIdx = approvedIndexMapRef.current[itemIdx];
                    if (origIdx === undefined) continue;
                    const arr = errsByOrigIdx.get(origIdx) || [];
                    arr.push(msg);
                    errsByOrigIdx.set(origIdx, arr);
                }

                setCandidates((prev) => prev?.map((c, i) => {
                    const errs = errsByOrigIdx.get(i);
                    if (!errs?.length) return c;
                    // Reprovar e mostrar agregação de erros no card
                    return { ...c, _approved: false, _error: errs.join(' · ') };
                }) || null);

                // Toast com resumo (até 5 cards listados)
                const affectedCards = Array.from(errsByOrigIdx.entries()).slice(0, 5);
                const summary = affectedCards.map(([idx, errs]) => {
                    const name = candidates?.[idx]?.name || `Modelo ${idx + 1}`;
                    return `• ${name}: ${errs[0]}`;
                }).join('\n');
                const more = errsByOrigIdx.size > 5 ? `\n…e mais ${errsByOrigIdx.size - 5}` : '';

                toast({
                    title: `${errsByOrigIdx.size} modelo${errsByOrigIdx.size === 1 ? '' : 's'} com erro`,
                    description: summary + more,
                    variant: 'destructive',
                });
            } else {
                // Sem `errors[]` (erro genérico do servidor / network)
                toast({
                    title: 'Falha ao criar',
                    description: data?.detail || e?.message || 'Erro inesperado',
                    variant: 'destructive',
                });
            }
        },
    });

    const approvedCount = useMemo(
        () => (candidates || []).filter((c) => c._approved).length,
        [candidates],
    );

    const toggleApprove = (i: number) => {
        setCandidates((prev) => prev!.map((c, idx) => (idx === i ? { ...c, _approved: !c._approved } : c)));
    };
    const discard = (i: number) => {
        setCandidates((prev) => prev!.filter((_, idx) => idx !== i));
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Importar / gerar em massa</DialogTitle>
                    <DialogDescription>
                        Destino: <strong>{folderLabel}</strong>. Cole um JSON, suba um arquivo
                        ou parta de um modelo. Revise e aprove antes de criar.
                    </DialogDescription>
                </DialogHeader>

                {/* ── Fase 1: entrada ── */}
                {!candidates ? (
                    <div className="space-y-4">
                        {/* Modo */}
                        <div className="inline-flex border rounded-md h-9 overflow-hidden text-xs">
                            {([
                                ['paste', 'Colar JSON'],
                                ['upload', 'Arquivo .json'],
                                ['fromModel', 'A partir de um modelo'],
                            ] as const).map(([val, label], i) => (
                                <button
                                    key={val} type="button" onClick={() => setMode(val)}
                                    className={`px-3 transition-colors ${
                                        mode === val ? 'bg-violet-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                                    } ${i > 0 ? 'border-l' : ''}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        {mode === 'upload' && (
                            <div
                                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-violet-300"
                                onClick={() => fileRef.current?.click()}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    const f = e.dataTransfer.files?.[0];
                                    if (f) handleFile(f);
                                }}
                            >
                                <Upload className="w-7 h-7 text-gray-400 mx-auto mb-2" />
                                <p className="text-sm text-gray-600">Clique ou arraste um arquivo .json</p>
                                <input
                                    ref={fileRef} type="file" accept=".json,application/json"
                                    className="hidden"
                                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                                />
                            </div>
                        )}

                        {mode === 'fromModel' && (
                            <div className="space-y-1.5">
                                <Label>Escolha o modelo base</Label>
                                {baseTemplates.length === 0 ? (
                                    <p className="text-xs text-gray-400 italic">
                                        Nenhum template nesta pasta pra usar como base.
                                    </p>
                                ) : (
                                    <div className="max-h-40 overflow-y-auto border rounded-md divide-y">
                                        {baseTemplates.map((t) => (
                                            <button
                                                key={t.id} type="button" onClick={() => loadFromModel(t)}
                                                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                                            >
                                                <Sparkles className="w-3.5 h-3.5 text-violet-500 shrink-0" />
                                                <span className="truncate">{t.name}</span>
                                                <span className="text-[10px] text-gray-400 ml-auto">
                                                    {t.template_type === 'generic' ? 'Card' : 'Texto'}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <p className="text-[11px] text-gray-500">
                                    Ao escolher, o JSON do modelo é colado abaixo (duplicado) — edite pra gerar variações.
                                </p>
                            </div>
                        )}

                        {/* Textarea (sempre visível, mostra o que vai ser processado) */}
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="json-input">JSON</Label>
                                <button
                                    type="button"
                                    onClick={() => setJsonText(SAMPLE)}
                                    className="text-[11px] text-violet-600 hover:underline"
                                >
                                    Inserir exemplo
                                </button>
                            </div>
                            <Textarea
                                id="json-input" value={jsonText} rows={12}
                                onChange={(e) => setJsonText(e.target.value)}
                                placeholder={SAMPLE}
                                className="font-mono text-xs"
                            />
                        </div>

                        {parseError && (
                            <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
                                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                {parseError}
                            </div>
                        )}

                        <DialogFooter>
                            <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancelar</Button>
                            <Button onClick={processJson} disabled={!jsonText.trim()}>
                                <FileJson className="w-4 h-4 mr-1.5" /> Processar
                            </Button>
                        </DialogFooter>
                    </div>
                ) : (
                    /* ── Fase 2: revisão ── */
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-600">
                                {candidates.length} {candidates.length === 1 ? 'modelo' : 'modelos'} ·{' '}
                                <strong className="text-violet-700">{approvedCount} aprovados</strong>
                            </p>
                            <Button variant="ghost" size="sm" onClick={() => setCandidates(null)}>
                                ← Voltar ao JSON
                            </Button>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto pr-1">
                            {candidates.map((c, i) => (
                                <div
                                    key={i}
                                    className={`border rounded-lg p-2 transition-colors ${
                                        c._approved ? 'border-violet-300 bg-violet-50/30' : 'border-gray-200 bg-gray-50 opacity-60'
                                    }`}
                                >
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-[11px] font-medium text-gray-700 truncate">{c.name}</span>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <button
                                                type="button" onClick={() => toggleApprove(i)}
                                                title={c._approved ? 'Desaprovar' : 'Aprovar'}
                                                className={`w-6 h-6 rounded flex items-center justify-center ${
                                                    c._approved ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-500'
                                                }`}
                                            >
                                                {c._approved ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                                            </button>
                                            <button
                                                type="button" onClick={() => discard(i)} title="Descartar"
                                                className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                    {c._error && (
                                        <p className="text-[10px] text-amber-600 mb-1">⚠ {c._error}</p>
                                    )}
                                    <MiniPreview c={c} />
                                </div>
                            ))}
                        </div>

                        <DialogFooter>
                            <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={mutation.isPending}>
                                Cancelar
                            </Button>
                            <Button
                                onClick={() => mutation.mutate()}
                                disabled={approvedCount === 0 || mutation.isPending}
                            >
                                {mutation.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                                Criar {approvedCount} {approvedCount === 1 ? 'modelo' : 'modelos'}
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function MiniPreview({ c }: { c: Candidate }) {
    const isCard = (c.template_type || 'generic') === 'generic';
    const buttons = (c.buttons || []).slice(0, 3);
    return (
        <div className="bg-white rounded-xl rounded-tl-sm border border-gray-200 overflow-hidden">
            {isCard && c.image_url && (
                <div className="aspect-[1.91/1] bg-gray-100 overflow-hidden">
                    <img src={c.image_url} alt="" loading="lazy" className="w-full h-full object-contain"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                </div>
            )}
            <div className="p-2 space-y-0.5">
                {isCard ? (
                    <>
                        {c.title && <p className="text-[12px] font-semibold text-gray-900 line-clamp-2 leading-tight">{c.title}</p>}
                        {c.subtitle && <p className="text-[10px] text-gray-600 line-clamp-2">{c.subtitle}</p>}
                    </>
                ) : (
                    <p className="text-[11px] text-gray-900 whitespace-pre-line line-clamp-4">
                        {c.text_content || c.title || '(sem texto)'}
                    </p>
                )}
            </div>
            {buttons.length > 0 && (
                <div className="border-t">
                    {buttons.map((b, i) => (
                        <div key={i} className={`px-2 py-1 text-center text-[10px] text-violet-600 font-medium truncate ${i > 0 ? 'border-t border-gray-100' : ''}`}>
                            {b.title || '(sem texto)'}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
